/**
 * Partnerships card — partner (supplier-org) side, Settings → Organization.
 *
 * Redeem a buyer's one-time invite code to activate the partner portal, and
 * manage the resulting partnerships (disconnect). Lives inside the org
 * settings <form>, so every button is type="button" and the code input traps
 * Enter — otherwise keystrokes would submit the whole settings form.
 *
 * Hides itself when SUPPLIER_PORTAL_ENABLED is off (getStatus → enabled:false).
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Handshake, Link2, Loader2, Unlink } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString() : '-';

export function PartnershipsCard() {
  const utils = trpc.useUtils();
  const statusQuery = trpc.supplierPortal.getStatus.useQuery(undefined, { staleTime: 30_000 });

  const [code, setCode] = useState('');
  const [disconnectTarget, setDisconnectTarget] = useState<{
    supplierId: string;
    buyerName: string | null;
  } | null>(null);

  const invalidate = () => void utils.supplierPortal.getStatus.invalidate();

  const redeem = trpc.supplierPortal.redeemSupplierInvite.useMutation({
    onSuccess: () => {
      toast.success('Partnership activated: the Partner Portal is now in your sidebar');
      setCode('');
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const disconnect = trpc.supplierPortal.disconnectFromBuyer.useMutation({
    onSuccess: () => {
      toast.success('Disconnected from partner');
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const status = statusQuery.data;
  if (!status?.enabled) return null;

  const handleRedeem = () => {
    const trimmed = code.trim();
    if (!trimmed) {
      toast.error('Paste an invite code first');
      return;
    }
    redeem.mutate({ inviteCode: trimmed });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-threadheads-purple/10">
              <Handshake className="h-5 w-5 text-threadheads-purple" />
            </div>
            <div>
              <CardTitle className="text-base">Partnerships</CardTitle>
              <CardDescription>
                Connect to a buyer who shares their purchase orders and product dev with
                your organisation.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Redeem an invite code */}
          <div className="space-y-2">
            <Label htmlFor="partner-invite-code">Invite code</Label>
            <div className="flex gap-2">
              <Input
                id="partner-invite-code"
                placeholder="PARTNER-XXXX-XXXX-XXXX-XXXX"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => {
                  // Inside the org-settings <form> — Enter must redeem, not save.
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleRedeem();
                  }
                }}
                className="font-mono text-sm"
                autoComplete="off"
              />
              <Button
                type="button"
                onClick={handleRedeem}
                disabled={!code.trim() || redeem.isPending}
                className="shrink-0"
              >
                {redeem.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Link2 className="h-3.5 w-3.5 mr-1.5" />
                )}
                Connect
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              The buyer generates this one-time code from their supplier page and shares it
              with you out-of-band. Codes expire after 14 days.
            </p>
          </div>

          {/* Active partnerships */}
          {status.partnerships.length > 0 && (
            <div className="space-y-2 pt-2 border-t">
              <p className="text-sm font-medium">Active partnerships</p>
              {status.partnerships.map((p) => (
                <div
                  key={`${p.buyerOrganizationId}-${p.supplierId}`}
                  className="flex items-center justify-between gap-3 rounded-md border p-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {p.buyerOrganizationName ?? 'Partner buyer'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Since {formatDate(p.redeemedAt)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() =>
                      setDisconnectTarget({
                        supplierId: p.supplierId,
                        buyerName: p.buyerOrganizationName,
                      })
                    }
                    disabled={disconnect.isPending}
                  >
                    <Unlink className="h-3.5 w-3.5 mr-1.5" />
                    Disconnect
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disconnect confirmation */}
      <AlertDialog
        open={disconnectTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDisconnectTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect from this buyer?</AlertDialogTitle>
            <AlertDialogDescription>
              Your organisation immediately loses access to{' '}
              <strong>{disconnectTarget?.buyerName ?? 'this buyer'}</strong>&apos;s shared
              orders and product-dev records. Reconnecting requires a fresh invite from them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (disconnectTarget) {
                  disconnect.mutate({ supplierId: disconnectTarget.supplierId });
                }
              }}
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
